import React from 'react';

import { FormControlLabel, Paper, Radio, RadioGroup, Stack, Typography } from '@mui/material';

import { formatDealOptionCurrency } from '../dealOptions/dealOptionsFormatters';

import { type EgsPayableConflict, type EgsPlanGates, type EgsSlotConflictOption } from './buildEgsApplyPlan';
import { EGS_SLOT_LABEL } from './egsUtils';
import { EGS_SELL_SOURCE_CURRENCY } from './egsWriteCurrency';
import type { EgsGuestSlot } from './types';

interface Props {
  costCurrency: string;
  gates: EgsPlanGates;
  roomRateId: string;
  setArchiveDecision: (rowId: string, decision: 'archived' | 'live') => void;
  setPayableAtProperty: (roomRateId: string, payableAtProperty: boolean) => void;
  setSlotWinner: (roomRateId: string, slot: EgsGuestSlot, rowId: string) => void;
}

function formatAmount(amount: number | undefined, currency: string) {
  return amount === undefined ? 'no amount' : formatDealOptionCurrency(currency, amount);
}

// Both figures, because two rows can agree on sell and differ on cost; picking on sell alone moves margin.
function formatSlotOption(option: EgsSlotConflictOption, costCurrency: string) {
  return `${option.roomLabel} · ${formatAmount(option.sell, EGS_SELL_SOURCE_CURRENCY)} sell, ${formatAmount(option.cost, costCurrency)} cost${option.collectedBy ? ` · ${option.collectedBy}` : ''}`;
}

// Both sentences where both apply: a room rate whose rows disagree still re-routes its kept charges.
function formatPayableConflictReason({ affectsKeptCharges, rowsDisagree }: EgsPayableConflict) {
  const sentences = [];

  if (rowsDisagree) {
    sentences.push(
      'Salesforce says different things for different guest types, but a room rate can only store one answer for all of them.',
    );
  } else {
    sentences.push('Salesforce and this room rate disagree about who collects it.');
  }

  if (affectsKeptCharges) {
    sentences.push(
      'Guest types we are leaving alone still have amounts, so your answer changes who collects those too.',
    );
  }

  return sentences.join(' ');
}

function GateQuestion({ children, title }: { children: React.ReactNode; title: string }) {
  return (
    <Paper sx={{ p: 2 }} variant="outlined">
      <Stack spacing={1}>
        <Typography variant="subtitle2">{title}</Typography>
        {children}
      </Stack>
    </Paper>
  );
}

// Answered questions stay on screen with the answer selected, so a decision can be changed.
export default function EgsGateResolutions({
  costCurrency,
  gates,
  roomRateId,
  setArchiveDecision,
  setPayableAtProperty,
  setSlotWinner,
}: Props) {
  return (
    <Stack spacing={1}>
      {gates.archiveDisagreements.map((disagreement) => (
        <GateQuestion key={disagreement.rowId} title={`Is "${disagreement.name}" still live?`}>
          <Typography color="text.secondary" variant="caption">
            Its name says it was retired, but Salesforce still has it marked as live. Only you know which is right.
          </Typography>
          <RadioGroup
            onChange={(event) => setArchiveDecision(disagreement.rowId, event.target.value as 'archived' | 'live')}
            value={disagreement.decision ?? ''}
          >
            <FormControlLabel control={<Radio size="small" />} label="Live, write its prices" value="live" />
            <FormControlLabel control={<Radio size="small" />} label="Retired, leave it out" value="archived" />
          </RadioGroup>
        </GateQuestion>
      ))}

      {gates.payableConflict && (
        <GateQuestion title="Who collects this surcharge?">
          <Typography color="text.secondary" variant="caption">
            {formatPayableConflictReason(gates.payableConflict)}
          </Typography>
          <RadioGroup
            onChange={(event) => setPayableAtProperty(roomRateId, event.target.value === 'true')}
            value={gates.payableConflict.resolvedTo === undefined ? '' : String(gates.payableConflict.resolvedTo)}
          >
            <FormControlLabel control={<Radio size="small" />} label="The guest pays the hotel" value="true" />
            <FormControlLabel control={<Radio size="small" />} label="We collect it" value="false" />
          </RadioGroup>
        </GateQuestion>
      )}

      {gates.slotConflicts.map((conflict) => (
        <GateQuestion key={conflict.slot} title={`Which price should ${EGS_SLOT_LABEL[conflict.slot]} use?`}>
          <Typography color="text.secondary" variant="caption">
            Salesforce has more than one price for this guest type. The one you pick is written, the rest are ignored
            for this room rate.
          </Typography>
          <RadioGroup
            onChange={(event) => setSlotWinner(roomRateId, conflict.slot, event.target.value)}
            value={conflict.resolvedRowId ?? ''}
          >
            {conflict.options.map((option) => (
              <FormControlLabel
                key={option.rowId}
                control={<Radio size="small" />}
                label={formatSlotOption(option, costCurrency)}
                value={option.rowId}
              />
            ))}
          </RadioGroup>
        </GateQuestion>
      ))}
    </Stack>
  );
}
